import React, { useEffect, useState, useMemo } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  ShieldCheck, AlertTriangle, CheckCircle2, XCircle, TrendingUp, TrendingDown,
  ArrowRight, Lock, Calendar, FileText, Building2, AlertOctagon, Loader2,
} from 'lucide-react';

// Phase 7.x · Fix #16 · Year-End Closing — UI.
// Shows a live preview of the would-be closing journal (revenue zeroed,
// expenses zeroed, net profit/loss → Retained Earnings) and gates the
// irreversible Close action behind a typed confirmation.

const fmtSAR = (n) =>
  `${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })} SAR`;

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - 4 + i);

const BlockerCard = ({ raw }) => {
  // Server emits compact strings like:
  //   unclosed_periods:2026-03,2026-09
  //   already_closed:<journal_id>
  //   Retained Earnings account ... not found
  let title = raw;
  let detail = null;
  if (raw.startsWith('unclosed_periods:')) {
    title = 'الأشهر التالية لم تُقفل / Following months are still open';
    detail = raw.replace('unclosed_periods:', '').split(',').filter(Boolean);
  } else if (raw.startsWith('already_closed:')) {
    title = 'السنة مُقفلة سابقاً / Year already closed';
    detail = [`Closing journal id: ${raw.replace('already_closed:', '')}`];
  }
  return (
    <div
      data-testid="ye-blocker"
      className="flex gap-3 p-3 rounded-lg bg-rose-50 border border-rose-200"
    >
      <AlertOctagon className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
      <div className="min-w-0">
        <p className="text-sm font-semibold text-rose-900">{title}</p>
        {detail && (
          <div className="flex flex-wrap gap-1 mt-1">
            {detail.map((d) => (
              <span
                key={d}
                className="font-mono text-xs px-2 py-0.5 rounded bg-white border border-rose-200 text-rose-700"
              >
                {d}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AccountRow = ({ row, side }) => {
  // For revenue: side='revenue' (credit-natured)
  // For expense: side='expense' (debit-natured)
  const amount = side === 'revenue' ? row.net_credit : row.net_debit;
  const color = side === 'revenue' ? 'emerald' : 'rose';
  return (
    <div
      data-testid={`ye-account-${row.account_id}`}
      className="flex items-center justify-between px-3 py-2 rounded border border-gray-100 hover:bg-gray-50 transition-colors"
    >
      <div className="min-w-0">
        <p className="font-mono text-xs text-gray-500">{row.account_number}</p>
        <p className="text-sm text-gray-900 truncate">{row.account_name}</p>
      </div>
      <span className={`text-sm font-semibold text-${color}-700 whitespace-nowrap`}>
        {fmtSAR(amount)}
      </span>
    </div>
  );
};

const ConfirmCloseModal = ({ open, onCancel, onConfirm, year, netProfit, direction, busy }) => {
  const [typed, setTyped] = useState('');
  const expectedToken = `CLOSE ${year}`;
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="ye-confirm-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-7 h-7 text-rose-600 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-bold text-rose-900">
              تأكيد إقفال السنة المالية {year}
            </h3>
            <p className="text-xs text-rose-700 mt-0.5">Confirm Year-End Close — IRREVERSIBLE</p>
          </div>
        </div>

        <div className="bg-rose-50 border-2 border-rose-200 rounded-lg p-4 space-y-2">
          <p className="text-sm font-semibold text-rose-900">⚠️ هذه العملية غير قابلة للتراجع</p>
          <ul className="text-xs text-rose-800 space-y-1 list-disc list-inside">
            <li>سيتم تصفير جميع حسابات الإيرادات والمصروفات.</li>
            <li>سيُرحَّل صافي {direction === 'profit' ? 'الربح' : 'الخسارة'} ({fmtSAR(Math.abs(netProfit))}) إلى الأرباح المحتجزة.</li>
            <li>سيُنشَأ قيد إقفال غير قابل للتعديل بتاريخ {year}-12-31.</li>
            <li>لا يمكن إعادة فتح السنة بدون تدخل يدوي.</li>
          </ul>
        </div>

        <div>
          <label className="text-xs text-gray-600 font-semibold">
            للتأكيد، اكتب: <span className="font-mono text-gray-900">{expectedToken}</span>
          </label>
          <input
            data-testid="ye-confirm-input"
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={expectedToken}
            className="mt-1 w-full px-3 py-2 border-2 border-gray-300 rounded-lg font-mono text-sm focus:border-rose-500 focus:outline-none"
            disabled={busy}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            data-testid="ye-cancel-btn"
            type="button"
            onClick={() => { setTyped(''); onCancel(); }}
            disabled={busy}
            className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            إلغاء / Cancel
          </button>
          <button
            data-testid="ye-confirm-close-btn"
            type="button"
            onClick={() => { onConfirm(); }}
            disabled={busy || typed !== expectedToken}
            className="px-4 py-2 rounded-lg bg-rose-600 text-white font-semibold hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            <ShieldCheck className="w-4 h-4" />
            تأكيد الإقفال
          </button>
        </div>
      </div>
    </div>
  );
};

const YearEnd = ({ user }) => {
  const [year, setYear] = useState(currentYear);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [closed, setClosed] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const canAct = user?.role === 'admin';

  const fetchPreview = async (y) => {
    setLoadingPreview(true);
    setPreview(null);
    try {
      const r = await axiosInstance.get(`/year-end/${y}/preview`);
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const fetchClosed = async () => {
    try {
      const r = await axiosInstance.get('/year-end');
      setClosed(r.data?.closed_years || []);
    } catch { /* empty list is fine */ }
  };

  useEffect(() => { fetchPreview(year); }, [year]);
  useEffect(() => { fetchClosed(); }, []);

  const handleClose = async () => {
    setBusy(true);
    try {
      const r = await axiosInstance.post(`/year-end/${year}/close`);
      toast.success(`السنة المالية ${year} أُقفلت بنجاح · Journal ${r.data.journal_no}`);
      setConfirmOpen(false);
      await fetchPreview(year);
      await fetchClosed();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Year-end close failed');
    } finally {
      setBusy(false);
    }
  };

  // ── Derived display state ────────────────────────────────────────────
  const isReady = preview?.ready === true;
  const alreadyClosed = preview?.already_closed;
  const direction = preview?.direction || 'profit';
  const np = preview?.net_profit || 0;
  const maxAmount = useMemo(() => {
    const all = [
      ...(preview?.revenue_accounts || []).map(a => a.net_credit),
      ...(preview?.expense_accounts || []).map(a => a.net_debit),
    ];
    return Math.max(1, ...all);
  }, [preview]);

  return (
    <div data-testid="year-end-page" className="space-y-6">
      {/* Header ─────────────────────────────────────────────────────── */}
      <div className="page-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="page-title flex items-center gap-2">
              <Calendar className="w-6 h-6 text-rose-600" />
              Year-End Closing · الإقفال السنوي
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              تصفير الإيرادات والمصروفات وترحيل صافي الربح إلى الأرباح المحتجزة.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">السنة المالية:</label>
            <select
              data-testid="ye-year-select"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="px-3 py-2 border-2 border-gray-300 rounded-lg font-mono text-sm focus:border-rose-500 focus:outline-none"
            >
              {YEAR_OPTIONS.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Loading state ──────────────────────────────────────────────── */}
      {loadingPreview && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 flex items-center justify-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading preview for {year}…
        </div>
      )}

      {/* Preview block ───────────────────────────────────────────────── */}
      {preview && !loadingPreview && (
        <>
          {/* Status banner */}
          <div
            data-testid="ye-status-banner"
            className={`rounded-xl p-4 flex items-start gap-3 ${
              alreadyClosed
                ? 'bg-gray-100 border border-gray-300'
                : isReady
                ? 'bg-emerald-50 border-2 border-emerald-200'
                : 'bg-rose-50 border-2 border-rose-200'
            }`}
          >
            {alreadyClosed ? (
              <Lock className="w-6 h-6 text-gray-600 flex-shrink-0" />
            ) : isReady ? (
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
            ) : (
              <XCircle className="w-6 h-6 text-rose-600 flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <p className={`font-semibold ${
                alreadyClosed ? 'text-gray-900'
                : isReady ? 'text-emerald-900'
                : 'text-rose-900'
              }`}>
                {alreadyClosed
                  ? `السنة المالية ${year} مُقفلة بالفعل / Year ${year} already closed`
                  : isReady
                  ? `جاهز للإقفال / Ready to close — net ${direction === 'profit' ? 'profit' : 'loss'} ${fmtSAR(Math.abs(np))}`
                  : `لا يمكن الإقفال — هناك ${preview.blockers?.length || 0} مُعطّل / Cannot close — ${preview.blockers?.length || 0} blocker(s)`}
              </p>
              {alreadyClosed?.closed_at && (
                <p className="text-xs text-gray-600 mt-1">
                  Closed at {alreadyClosed.closed_at} · Journal {alreadyClosed.journal_no}
                </p>
              )}
            </div>

            {!alreadyClosed && (
              <button
                data-testid="ye-open-confirm-btn"
                type="button"
                disabled={!canAct || !isReady}
                onClick={() => setConfirmOpen(true)}
                className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 whitespace-nowrap ${
                  canAct && isReady
                    ? 'bg-rose-600 text-white hover:bg-rose-700'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                <ShieldCheck className="w-4 h-4" />
                إقفال السنة / Close Year
              </button>
            )}
          </div>

          {/* Blockers list */}
          {!isReady && !alreadyClosed && preview.blockers?.length > 0 && (
            <div className="space-y-2">
              {preview.blockers.map((b, i) => (
                <BlockerCard key={i} raw={b} />
              ))}
            </div>
          )}

          {/* Net Profit / Loss hero ──────────────────────────────────── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              data-testid="ye-total-revenue"
              className="bg-emerald-50 border border-emerald-200 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold uppercase">
                <TrendingUp className="w-4 h-4" />
                إجمالي الإيرادات / Total Revenue
              </div>
              <p className="text-2xl font-bold text-emerald-900 mt-2">
                {fmtSAR(preview.total_revenue)}
              </p>
              <p className="text-xs text-emerald-700 mt-1">
                {preview.revenue_accounts?.length || 0} account(s)
              </p>
            </div>

            <div
              data-testid="ye-total-expense"
              className="bg-rose-50 border border-rose-200 rounded-xl p-4"
            >
              <div className="flex items-center gap-2 text-rose-700 text-xs font-semibold uppercase">
                <TrendingDown className="w-4 h-4" />
                إجمالي المصروفات / Total Expense
              </div>
              <p className="text-2xl font-bold text-rose-900 mt-2">
                {fmtSAR(preview.total_expense)}
              </p>
              <p className="text-xs text-rose-700 mt-1">
                {preview.expense_accounts?.length || 0} account(s)
              </p>
            </div>

            <div
              data-testid="ye-net-profit"
              className={`rounded-xl p-4 border-2 ${
                direction === 'profit'
                  ? 'bg-indigo-50 border-indigo-300'
                  : 'bg-amber-50 border-amber-300'
              }`}
            >
              <div className={`flex items-center gap-2 text-xs font-semibold uppercase ${
                direction === 'profit' ? 'text-indigo-700' : 'text-amber-700'
              }`}>
                {direction === 'profit' ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                صافي {direction === 'profit' ? 'الربح' : 'الخسارة'} / Net {direction === 'profit' ? 'Profit' : 'Loss'}
              </div>
              <p className={`text-2xl font-bold mt-2 ${
                direction === 'profit' ? 'text-indigo-900' : 'text-amber-900'
              }`}>
                {fmtSAR(Math.abs(np))}
              </p>
              <p className={`text-xs mt-1 ${
                direction === 'profit' ? 'text-indigo-700' : 'text-amber-700'
              }`}>
                → Retained Earnings
              </p>
            </div>
          </div>

          {/* Accounts decomposition ─────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
              <h4 className="font-semibold text-emerald-900 flex items-center gap-2">
                <TrendingUp className="w-4 h-4" />
                Revenue Accounts ({preview.revenue_accounts?.length || 0})
              </h4>
              {(preview.revenue_accounts || []).length === 0 ? (
                <p className="text-sm text-gray-400 italic py-4">لا يوجد نشاط إيرادات في السنة.</p>
              ) : (
                <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                  {preview.revenue_accounts.map((row) => (
                    <div key={row.account_id}>
                      <AccountRow row={row} side="revenue" />
                      <div className="h-1 rounded bg-emerald-100 mt-1 overflow-hidden">
                        <div
                          className="h-full bg-emerald-500"
                          style={{ width: `${Math.min(100, (row.net_credit / maxAmount) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-2">
              <h4 className="font-semibold text-rose-900 flex items-center gap-2">
                <TrendingDown className="w-4 h-4" />
                Expense Accounts ({preview.expense_accounts?.length || 0})
              </h4>
              {(preview.expense_accounts || []).length === 0 ? (
                <p className="text-sm text-gray-400 italic py-4">لا يوجد نشاط مصروفات في السنة.</p>
              ) : (
                <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                  {preview.expense_accounts.map((row) => (
                    <div key={row.account_id}>
                      <AccountRow row={row} side="expense" />
                      <div className="h-1 rounded bg-rose-100 mt-1 overflow-hidden">
                        <div
                          className="h-full bg-rose-500"
                          style={{ width: `${Math.min(100, (row.net_debit / maxAmount) * 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RE routing diagram ─────────────────────────────────────── */}
          {preview.retained_earnings_account && (
            <div
              data-testid="ye-routing-diagram"
              className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5"
            >
              <h4 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                مسار صافي الربح / Net Profit Routing
              </h4>
              <div className="flex items-center justify-center gap-4 flex-wrap">
                <div className="bg-white rounded-lg border border-emerald-300 px-4 py-3 text-center min-w-[180px]">
                  <p className="text-xs text-emerald-700 uppercase font-semibold">Revenue (zeroed)</p>
                  <p className="text-lg font-bold text-emerald-900">{fmtSAR(preview.total_revenue)}</p>
                </div>
                <span className="text-gray-400">−</span>
                <div className="bg-white rounded-lg border border-rose-300 px-4 py-3 text-center min-w-[180px]">
                  <p className="text-xs text-rose-700 uppercase font-semibold">Expense (zeroed)</p>
                  <p className="text-lg font-bold text-rose-900">{fmtSAR(preview.total_expense)}</p>
                </div>
                <ArrowRight className="w-6 h-6 text-indigo-500 flex-shrink-0" />
                <div className={`rounded-lg border-2 px-4 py-3 text-center min-w-[200px] ${
                  direction === 'profit'
                    ? 'bg-indigo-100 border-indigo-400'
                    : 'bg-amber-100 border-amber-400'
                }`}>
                  <p className={`text-xs uppercase font-semibold ${
                    direction === 'profit' ? 'text-indigo-700' : 'text-amber-700'
                  }`}>
                    {preview.retained_earnings_account.account_name}
                  </p>
                  <p className="font-mono text-[10px] text-gray-500">
                    {preview.retained_earnings_account.account_number}
                  </p>
                  <p className={`text-lg font-bold mt-1 ${
                    direction === 'profit' ? 'text-indigo-900' : 'text-amber-900'
                  }`}>
                    {direction === 'profit' ? '+' : '−'} {fmtSAR(Math.abs(np))}
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* History of closed years ───────────────────────────────────────── */}
      {closed.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-500" />
            السنوات المُقفلة سابقاً / Previously closed years
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b">
                  <th className="text-left py-2">Year</th>
                  <th className="text-right py-2">Revenue</th>
                  <th className="text-right py-2">Expense</th>
                  <th className="text-right py-2">Net P/L</th>
                  <th className="text-left py-2">Journal</th>
                  <th className="text-left py-2">Closed By</th>
                  <th className="text-left py-2">Closed At</th>
                </tr>
              </thead>
              <tbody>
                {closed.map((c) => (
                  <tr
                    key={c.id}
                    data-testid={`ye-history-${c.year}`}
                    className="border-b last:border-0 hover:bg-gray-50"
                  >
                    <td className="py-2 font-mono font-semibold">{c.year}</td>
                    <td className="text-right text-emerald-700">{fmtSAR(c.total_revenue)}</td>
                    <td className="text-right text-rose-700">{fmtSAR(c.total_expense)}</td>
                    <td className={`text-right font-semibold ${c.net_profit >= 0 ? 'text-indigo-700' : 'text-amber-700'}`}>
                      {c.net_profit >= 0 ? '+' : '−'} {fmtSAR(Math.abs(c.net_profit))}
                    </td>
                    <td className="font-mono text-xs text-gray-600">{c.journal_no}</td>
                    <td className="text-xs text-gray-600">{c.closed_by_name}</td>
                    <td className="text-xs text-gray-500">{(c.closed_at || '').slice(0, 19).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmCloseModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={handleClose}
        year={year}
        netProfit={np}
        direction={direction}
        busy={busy}
      />
    </div>
  );
};

export default YearEnd;
